import Head from 'next/head'
import { Course } from '@components/courses/course-card'
import { CourseGroup } from '@lib/utils/course-categorization'

const CODINGAL_ORIGIN = 'https://www.codingal.com'

const toAbsoluteCodingalUrl = (
  url: string | null | undefined
): string | null => {
  if (!url) return null
  try {
    const { pathname, search } = new URL(url, CODINGAL_ORIGIN)
    return `${CODINGAL_ORIGIN}${pathname}${search}`
  } catch {
    return null
  }
}

const dedupeCoursesByUrl = (courses: Course[]): Course[] => {
  const seenUrls = new Set<string>()
  return courses.filter((course) => {
    const url = course.landingPageUrl ?? course.trialCourseSlug
    if (!url || seenUrls.has(url)) return false
    seenUrls.add(url)
    return true
  })
}

const buildCourseListItem = (course: Course, position: number) => ({
  '@type': 'ListItem',
  position,
  item: {
    '@type': 'Course',
    name: course.title,
    ...(course.description && { description: course.description }),
    url:
      toAbsoluteCodingalUrl(course.landingPageUrl) ??
      `${CODINGAL_ORIGIN}/trial-class/booking/?course=${course.trialCourseSlug}`,
    ...(course.thumbnailLandscape && { image: course.thumbnailLandscape }),
    ...(course.tags?.length && {
      keywords: course.tags.map((tag) => tag.title).join(', '),
    }),
    provider: {
      '@type': 'Organization',
      name: 'Codingal',
      sameAs: CODINGAL_ORIGIN,
    },
    ...(course.paymentPageUrl && {
      offers: {
        '@type': 'Offer',
        url: toAbsoluteCodingalUrl(course.paymentPageUrl),
        category: 'Paid',
      },
    }),
    ...((course.ageRange || course.gradeRange) && {
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: 'student',
        ...(course.ageRange && { typicalAgeRange: course.ageRange }),
        ...(course.gradeRange && { educationalLevel: course.gradeRange }),
      },
    }),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      ...(typeof course.totalLessonsCount === 'number' &&
        course.totalLessonsCount > 0 && {
          numberOfSessions: course.totalLessonsCount,
        }),
      ...(course.estimatedCompletionTime && {
        courseSchedule: {
          '@type': 'Schedule',
          description: course.estimatedCompletionTime,
        },
      }),
    },
  },
})

type CourseCollectionsJsonLdProps = {
  groups: CourseGroup[]
  pageName: string
  pageUrl: string
}

const CourseCollectionsJsonLd = ({
  groups,
  pageName,
  pageUrl,
}: CourseCollectionsJsonLdProps) => {
  const nonEmptyGroups = groups.filter((group) => group.courses.length > 0)
  if (!nonEmptyGroups.length) return null

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageName,
    url: pageUrl,
    hasPart: nonEmptyGroups.map((group) => {
      const uniqueCourses = dedupeCoursesByUrl(group.courses)
      return {
        '@type': 'Collection',
        name: group.title,
        hasPart: {
          '@type': 'ItemList',
          name: group.title,
          itemListElement: uniqueCourses.map((course, index) =>
            buildCourseListItem(course, index + 1)
          ),
        },
      }
    }),
  }

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </Head>
  )
}

export default CourseCollectionsJsonLd
